import { newServer } from 'constants/constants';

export interface UserRequest {
  id?: number;
  finished?: boolean;
  deferred?: boolean;
  status_dwolla_id?: number | null;
  status_usio_id?: number | null;
  business_type?: string | null;
  info_dwolla?: Record<string, unknown> | null;
  info_usio?: Record<string, unknown> | null;
  subdomain?: { subdomain?: string } | null;
  providerChoice?: string | null;
  [key: string]: unknown;
}

export type FetchRequestByUserResult =
  | { status: 'ok'; request: UserRequest }
  | { status: 'missing' }
  | { status: 'expired' }
  | { status: 'error'; message: string };

interface RequestOwner {
  user_id?: string | number | null;
  token?: string | null;
}

/**
 * Propósito: Lee el body de `POST /requests/user/:id` sin asumir JSON.
 * El 400 de "solicitud inexistente" llega en texto plano y `response.json()`
 * tiraba, dejando al wizard en un estado mentiroso (FundingStep "finished").
 */
const parseRequestBody = async (
  response: Response,
): Promise<Record<string, unknown> | null> => {
  const text = await response.text();
  if (!text) {
    /**
     * Output:
     * @returns null — Respuesta vacía; el caller decide según el status HTTP
     */
    return null;
  }

  try {
    /**
     * Output:
     * @returns object — Body JSON del backend
     */
    return JSON.parse(text);
  } catch (_error) {
    /**
     * Output:
     * @returns object — Texto plano envuelto para no perder el mensaje
     */
    return { message: text };
  }
};

/**
 * Propósito: Trae la solicitud de enrolamiento del usuario autenticado.
 * Distingue "todavía no hay request" (postulación nueva) de un error real,
 * para no tratar un 400 esperado como si la cuenta ya estuviera lista.
 */
export const fetchRequestByUser = async (
  owner: RequestOwner | null | undefined,
): Promise<FetchRequestByUserResult> => {
  if (!owner?.user_id || !owner?.token) {
    /**
     * Output:
     * @returns { status: 'missing' } — Sin sesión no hay solicitud que consultar
     */
    return { status: 'missing' };
  }

  try {
    const response = await fetch(`${newServer}requests/user/${owner.user_id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${owner.token}`,
      },
    });

    const payload = await parseRequestBody(response);
    const message =
      (typeof payload?.message === 'string' && payload.message) ||
      (typeof payload?.status === 'string' && payload.status) ||
      '';

    if (response.status === 401 || response.status === 403 || message === 'JWT_EXPIRED') {
      /**
       * Output:
       * @returns { status: 'expired' } — Token inválido; el caller debe cerrar sesión
       */
      return { status: 'expired' };
    }

    if (response.ok && payload && payload.exists !== false) {
      /**
       * Output:
       * @returns { status: 'ok', request } — Solicitud existente con su estado de enrolamiento
       */
      return { status: 'ok', request: payload as UserRequest };
    }

    if (response.status === 400 || response.status === 404 || payload?.exists === false) {
      /**
       * Output:
       * @returns { status: 'missing' } — Usuario sin request: postulación nueva
       */
      return { status: 'missing' };
    }

    /**
     * Output:
     * @returns { status: 'error', message } — Fallo inesperado del API
     */
    return {
      status: 'error',
      message: message || `Request lookup failed (${response.status})`,
    };
  } catch (error: unknown) {
    /**
     * Output:
     * @returns { status: 'error', message } — Error de red u otra excepción
     */
    return {
      status: 'error',
      message: error instanceof Error ? error.message : 'We could not load your application.',
    };
  }
};
